import { useState, useEffect, useCallback } from "react";
import {
  Package,
  Home,
  Map,
  Settings,
  BarChart3,
  Shield,
  Wrench,
  FileText,
  ChevronRight,
  ChevronLeft,
  Plus,
  Edit2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  RefreshCw,
  X,
  Save,
  AlertCircle,
  Layers,
  Hash,
  Link,
  Image,
  Check,
  Trash2,
} from "lucide-react";
import Swal from "sweetalert2";

// ── Icono según slug ──────────────────────────────────────
const iconMap = {
  principal: Home,
  operaciones: Map,
  viajes: Package,
  gestion: Settings,
  reportes: BarChart3,
  sistema: Shield,
  mantenimiento: Wrench,
  documentos: FileText,
  "viajes-modulo": Package,
  "modulos-empresa": Layers,
};
const getIcon = (slug) => iconMap[slug] || Package;

const MODULE_COLORS = [
  {
    bg: "from-blue-600 to-blue-700",
    soft: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    bg: "from-emerald-600 to-emerald-700",
    soft: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    bg: "from-violet-600 to-violet-700",
    soft: "bg-violet-50 text-violet-700 border-violet-200",
  },
  {
    bg: "from-orange-500 to-orange-600",
    soft: "bg-orange-50 text-orange-700 border-orange-200",
  },
  {
    bg: "from-rose-600 to-rose-700",
    soft: "bg-rose-50 text-rose-700 border-rose-200",
  },
  {
    bg: "from-cyan-600 to-cyan-700",
    soft: "bg-cyan-50 text-cyan-700 border-cyan-200",
  },
];

const getColor = (index) => MODULE_COLORS[index % MODULE_COLORS.length];

// ── API helpers ───────────────────────────────────────────
const getApiUrl = () =>
  process.env.REACT_APP_API_URL || "http://localhost:8000/api";
const getHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const api = async (url, options = {}) => {
  const res = await fetch(`${getApiUrl()}${url}`, {
    headers: getHeaders(),
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || "Error en la petición");
  return data;
};

// ── Toast helper ──────────────────────────────────────────
const toast = (icon, title) =>
  Swal.fire({
    toast: true,
    position: "top-end",
    icon,
    title,
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
  });

// ══════════════════════════════════════════════════════════
// MODAL — Crear / Editar Módulo o Menú
// ══════════════════════════════════════════════════════════
const FormModal = ({ mode, parentModulo, editItem, onClose, onSaved }) => {
  const isEdit = !!editItem;
  const isModulo = mode === "modulo";

  const [form, setForm] = useState({
    name: editItem?.name || "",
    slug: editItem?.slug || "",
    icon: editItem?.icon || "",
    route: editItem?.route || "",
    es_modulo: isModulo,
    parent_id: isModulo ? null : parentModulo?.id,
    active: editItem?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Auto-generar slug desde name
  const handleName = (val) => {
    const slug = val
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-");
    setForm((f) => ({ ...f, name: val, slug: isEdit ? f.slug : slug }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "El nombre es obligatorio";
    if (!form.slug.trim()) e.slug = "El slug es obligatorio";
    if (!/^[a-z0-9-]+$/.test(form.slug))
      e.slug = "Solo letras minúsculas, números y guiones";
    if (!isModulo && !form.route?.trim())
      e.route = "La ruta es obligatoria para menús";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const body = { ...form };
      if (isEdit) {
        await api(`/menus/${editItem.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        toast("success", isModulo ? "Módulo actualizado" : "Menú actualizado");
      } else {
        await api("/menus", {
          method: "POST",
          body: JSON.stringify(body),
        });
        toast("success", isModulo ? "Módulo creado" : "Menú creado");
      }
      onSaved();
    } catch (err) {
      toast("error", err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div
          className={`p-5 bg-gradient-to-r ${isModulo ? "from-indigo-600 to-violet-600" : "from-slate-700 to-slate-800"}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                {isModulo ? (
                  <Layers className="h-5 w-5 text-white" />
                ) : (
                  <Hash className="h-5 w-5 text-white" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm">
                  {isEdit ? "Editar" : "Nuevo"} {isModulo ? "Módulo" : "Menú"}
                </h3>
                {!isModulo && parentModulo && (
                  <p className="text-white/70 text-xs mt-0.5">
                    en {parentModulo.name}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Nombre */}
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1.5">
              Nombre *
            </label>
            <input
              value={form.name}
              onChange={(e) => handleName(e.target.value)}
              placeholder={
                isModulo
                  ? "ej: Documentos y Vigencia"
                  : "ej: Tipos de Documento"
              }
              className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-all
                ${errors.name ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50"}`}
            />
            {errors.name && (
              <p className="text-xs text-red-500 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Slug */}
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1.5">
              <Hash className="h-3 w-3 inline mr-1" />
              Slug *
            </label>
            <input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="ej: documentos-vigencia"
              className={`w-full px-3 py-2 text-sm border rounded-lg outline-none font-mono transition-all
                ${errors.slug ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50"}`}
            />
            {errors.slug && (
              <p className="text-xs text-red-500 mt-1">{errors.slug}</p>
            )}
          </div>

          {/* Icono */}
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1.5">
              <Image className="h-3 w-3 inline mr-1" />
              Icono (Lucide)
            </label>
            <input
              value={form.icon}
              onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
              placeholder="ej: FileText, Package, Shield"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition-all"
            />
            <p className="text-xs text-gray-400 mt-1">
              Nombre exacto del ícono de lucide-react
            </p>
          </div>

          {/* Ruta — solo para menús */}
          {!isModulo && (
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1.5">
                <Link className="h-3 w-3 inline mr-1" />
                Ruta *
              </label>
              <input
                value={form.route}
                onChange={(e) =>
                  setForm((f) => ({ ...f, route: e.target.value }))
                }
                placeholder="ej: /documentos/tipos"
                className={`w-full px-3 py-2 text-sm border rounded-lg outline-none font-mono transition-all
                  ${errors.route ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50"}`}
              />
              {errors.route && (
                <p className="text-xs text-red-500 mt-1">{errors.route}</p>
              )}
            </div>
          )}

          {/* Activo */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-700">Activo</p>
              <p className="text-xs text-gray-400">Visible en el sistema</p>
            </div>
            <button
              onClick={() => setForm((f) => ({ ...f, active: !f.active }))}
              className="transition-colors"
            >
              {form.active ? (
                <ToggleRight className="h-7 w-7 text-emerald-500" />
              ) : (
                <ToggleLeft className="h-7 w-7 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700
              text-white rounded-lg transition-colors disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// VISTA 2 — Detalle de un módulo con sus menús
// ══════════════════════════════════════════════════════════
const ModuloDetalle = ({ modulo, colorIndex, onBack, onRefresh }) => {
  const [menus, setMenus] = useState(modulo.children || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const colors = getColor(colorIndex);
  const Icon = getIcon(modulo.slug);

  const cargarMenus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/menus?flat=true&parent_id=${modulo.id}`);
      // Filtrar hijos directos del módulo
      const hijos = (data.data || []).filter((m) => m.parent_id === modulo.id);
      setMenus(hijos);
    } catch {
      toast("error", "Error al cargar menús");
    } finally {
      setLoading(false);
    }
  }, [modulo.id]);

  useEffect(() => {
    cargarMenus();
  }, [cargarMenus]);

  const handleToggle = async (menu) => {
    setTogglingId(menu.id);
    try {
      await api(`/menus/${menu.id}/toggle-active`, { method: "PATCH" });
      setMenus((prev) =>
        prev.map((m) => (m.id === menu.id ? { ...m, active: !m.active } : m)),
      );
      toast("success", menu.active ? "Menú desactivado" : "Menú activado");
    } catch (err) {
      toast("error", err.message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (menu) => {
    const confirm = await Swal.fire({
      title: `¿Eliminar "${menu.name}"?`,
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sí, eliminar",
    });
    if (!confirm.isConfirmed) return;
    try {
      await api(`/menus/${menu.id}`, { method: "DELETE" });
      setMenus((prev) => prev.filter((m) => m.id !== menu.id));
      toast("success", "Menú eliminado");
    } catch (err) {
      toast("error", err.message);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header módulo */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className={`bg-gradient-to-r ${colors.bg} p-5`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <ChevronLeft className="h-4 w-4 text-white" />
              </button>
              <div className="p-2.5 bg-white/20 rounded-xl">
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-white text-base">
                  {modulo.name}
                </h2>
                <p className="text-white/70 text-xs font-mono mt-0.5">
                  {modulo.slug}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={cargarMenus}
                disabled={loading}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <RefreshCw
                  className={`h-4 w-4 text-white ${loading ? "animate-spin" : ""}`}
                />
              </button>
              <button
                onClick={() => {
                  setEditItem(null);
                  setShowModal(true);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30
                  text-white text-sm rounded-lg transition-colors font-medium"
              >
                <Plus className="h-4 w-4" />
                Agregar menú
              </button>
            </div>
          </div>
        </div>

        {/* Stats rápidas */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-6 text-xs text-gray-500">
          <span>
            <strong className="text-gray-800">{menus.length}</strong> menús
          </span>
          <span>
            <strong className="text-emerald-600">
              {menus.filter((m) => m.active).length}
            </strong>{" "}
            activos
          </span>
          <span>
            <strong className="text-gray-400">
              {menus.filter((m) => !m.active).length}
            </strong>{" "}
            inactivos
          </span>
        </div>
      </div>

      {/* Lista de menús */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
            <span className="ml-2 text-sm text-gray-500">
              Cargando menús...
            </span>
          </div>
        ) : menus.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Hash className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-600">Sin menús</p>
            <p className="text-xs text-gray-400 mt-1">
              Agrega el primer menú a este módulo
            </p>
            <button
              onClick={() => {
                setEditItem(null);
                setShowModal(true);
              }}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white
                text-sm rounded-lg mx-auto hover:bg-indigo-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Agregar menú
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {menus.map((menu, i) => (
              <div
                key={menu.id}
                className={`flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50/50 transition-colors
                  ${!menu.active ? "opacity-50" : ""}`}
              >
                {/* Número */}
                <span className="text-xs text-gray-400 w-5 text-center font-mono">
                  {i + 1}
                </span>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {menu.name}
                    </p>
                    {!menu.active && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                        inactivo
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-400 font-mono">
                      {menu.slug}
                    </span>
                    {menu.route && (
                      <span className="text-xs text-indigo-400 font-mono truncate">
                        {menu.route}
                      </span>
                    )}
                  </div>
                </div>

                {/* Icono nombre */}
                {menu.icon && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono hidden sm:block">
                    {menu.icon}
                  </span>
                )}

                {/* Acciones */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      setEditItem(menu);
                      setShowModal(true);
                    }}
                    className="p-1.5 hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>

                  <button
                    onClick={() => handleToggle(menu)}
                    disabled={togglingId === menu.id}
                    className="p-1 transition-colors disabled:opacity-50"
                    title={menu.active ? "Desactivar" : "Activar"}
                  >
                    {togglingId === menu.id ? (
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    ) : menu.active ? (
                      <ToggleRight className="h-6 w-6 text-emerald-500 hover:text-emerald-600" />
                    ) : (
                      <ToggleLeft className="h-6 w-6 text-gray-400 hover:text-gray-500" />
                    )}
                  </button>

                  <button
                    onClick={() => handleDelete(menu)}
                    className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal crear/editar menú */}
      {showModal && (
        <FormModal
          mode="menu"
          parentModulo={modulo}
          editItem={editItem}
          onClose={() => {
            setShowModal(false);
            setEditItem(null);
          }}
          onSaved={() => {
            setShowModal(false);
            setEditItem(null);
            cargarMenus();
            onRefresh();
          }}
        />
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// VISTA 1 — Lista de módulos (principal)
// ══════════════════════════════════════════════════════════
const ModulosEmpresaGestion = () => {
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vistaDetalle, setVistaDetalle] = useState(null); // { modulo, colorIndex }
  const [showModalModulo, setShowModalModulo] = useState(false);
  const [editModulo, setEditModulo] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const cargarModulos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api("/menus?es_modulo=true");
      const lista = (data.data || []).filter(
        (m) => m.es_modulo === true || m.es_modulo == "1",
      );
      setModulos(lista);
    } catch {
      toast("error", "Error al cargar módulos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarModulos();
  }, [cargarModulos]);

  const handleToggleModulo = async (modulo) => {
    setTogglingId(modulo.id);
    try {
      await api(`/menus/${modulo.id}/toggle-active`, { method: "PATCH" });
      setModulos((prev) =>
        prev.map((m) => (m.id === modulo.id ? { ...m, active: !m.active } : m)),
      );
      toast(
        "success",
        modulo.active ? "Módulo desactivado" : "Módulo activado",
      );
    } catch (err) {
      toast("error", err.message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteModulo = async (modulo) => {
    const confirm = await Swal.fire({
      title: `¿Eliminar módulo "${modulo.name}"?`,
      html: `<p class="text-sm text-gray-600">Se eliminarán también todos sus menús asociados.<br>Esta acción no se puede deshacer.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Eliminar módulo",
    });
    if (!confirm.isConfirmed) return;
    try {
      await api(`/menus/${modulo.id}`, { method: "DELETE" });
      setModulos((prev) => prev.filter((m) => m.id !== modulo.id));
      toast("success", "Módulo eliminado");
    } catch (err) {
      toast("error", err.message);
    }
  };

  // Si estamos en vista detalle
  if (vistaDetalle) {
    return (
      <ModuloDetalle
        modulo={vistaDetalle.modulo}
        colorIndex={vistaDetalle.colorIndex}
        onBack={() => setVistaDetalle(null)}
        onRefresh={cargarModulos}
      />
    );
  }

  // ── Vista principal ────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-600 rounded-xl">
              <Layers className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-base">
                Módulos del Sistema
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {modulos.length} módulos contratables · gestiona menús por
                módulo
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={cargarModulos}
              disabled={loading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={() => {
                setEditModulo(null);
                setShowModalModulo(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                text-white text-sm rounded-xl transition-colors font-medium"
            >
              <Plus className="h-4 w-4" />
              Nuevo módulo
            </button>
          </div>
        </div>
      </div>

      {/* Banner info */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
        <div>
          <strong>Módulos contratables</strong> — Solo los módulos marcados como
          <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded font-mono">
            es_modulo=true
          </code>
          aparecen aquí. Desde <strong>Empresas → botón módulos</strong> puedes
          habilitar o deshabilitar cada módulo por empresa. El Admin Local solo
          verá los módulos que su empresa tenga activos.
        </div>
      </div>

      {/* Grid de módulos */}
      {loading ? (
        <div className="flex items-center justify-center py-20 bg-white rounded-2xl border border-gray-100">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          <span className="ml-2 text-sm text-gray-500">
            Cargando módulos...
          </span>
        </div>
      ) : modulos.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Layers className="h-7 w-7 text-indigo-400" />
          </div>
          <p className="font-medium text-gray-700">Sin módulos contratables</p>
          <p className="text-sm text-gray-400 mt-1 mb-4">
            Crea el primer módulo o ejecuta los SQLs de migración para marcar
            los existentes
          </p>
          <button
            onClick={() => setShowModalModulo(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white
              text-sm rounded-xl mx-auto hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Crear primer módulo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {modulos.map((modulo, index) => {
            const Icon = getIcon(modulo.slug);
            const colors = getColor(index);
            const menusCount = modulo.children?.length ?? 0;
            const menusActivos =
              modulo.children?.filter((c) => c.active).length ?? 0;

            return (
              <div
                key={modulo.id}
                className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden
                  transition-all hover:shadow-md hover:-translate-y-0.5
                  ${!modulo.active ? "opacity-60" : ""}`}
              >
                {/* Card header con gradiente */}
                <div className={`bg-gradient-to-r ${colors.bg} p-4`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-white/20 rounded-xl">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-sm">
                          {modulo.name}
                        </h3>
                        <p className="text-white/60 text-xs font-mono mt-0.5">
                          {modulo.slug}
                        </p>
                      </div>
                    </div>
                    {!modulo.active && (
                      <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">
                        inactivo
                      </span>
                    )}
                  </div>
                </div>

                {/* Card body */}
                <div className="p-4 space-y-3">
                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>
                      <strong className="text-gray-800">{menusCount}</strong>{" "}
                      menús
                    </span>
                    {menusCount > 0 && (
                      <span>
                        <strong className="text-emerald-600">
                          {menusActivos}
                        </strong>{" "}
                        activos
                      </span>
                    )}
                  </div>

                  {/* Preview menús */}
                  {menusCount > 0 && (
                    <div className="space-y-1">
                      {(modulo.children || []).slice(0, 3).map((child) => (
                        <div
                          key={child.id}
                          className="flex items-center gap-2 text-xs text-gray-500 py-0.5"
                        >
                          <div
                            className={`w-1.5 h-1.5 rounded-full flex-shrink-0
                            ${child.active ? "bg-emerald-400" : "bg-gray-300"}`}
                          />
                          <span className="truncate">{child.name}</span>
                        </div>
                      ))}
                      {menusCount > 3 && (
                        <p className="text-xs text-gray-400 pl-3.5">
                          +{menusCount - 3} más...
                        </p>
                      )}
                    </div>
                  )}

                  {/* Acciones */}
                  <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                    <button
                      onClick={() =>
                        setVistaDetalle({ modulo, colorIndex: index })
                      }
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs
                        font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    >
                      Ver menús <ChevronRight className="h-3.5 w-3.5" />
                    </button>

                    <button
                      onClick={() => {
                        setEditModulo(modulo);
                        setShowModalModulo(true);
                      }}
                      className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600
                        rounded-lg transition-colors"
                      title="Editar módulo"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>

                    <button
                      onClick={() => handleToggleModulo(modulo)}
                      disabled={togglingId === modulo.id}
                      className="p-1 disabled:opacity-50 transition-colors"
                      title={modulo.active ? "Desactivar" : "Activar"}
                    >
                      {togglingId === modulo.id ? (
                        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                      ) : modulo.active ? (
                        <ToggleRight className="h-6 w-6 text-emerald-500" />
                      ) : (
                        <ToggleLeft className="h-6 w-6 text-gray-400" />
                      )}
                    </button>

                    <button
                      onClick={() => handleDeleteModulo(modulo)}
                      className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500
                        rounded-lg transition-colors"
                      title="Eliminar módulo"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal crear/editar módulo */}
      {showModalModulo && (
        <FormModal
          mode="modulo"
          parentModulo={null}
          editItem={editModulo}
          onClose={() => {
            setShowModalModulo(false);
            setEditModulo(null);
          }}
          onSaved={() => {
            setShowModalModulo(false);
            setEditModulo(null);
            cargarModulos();
          }}
        />
      )}
    </div>
  );
};

export default ModulosEmpresaGestion;
